import { useCallback, useEffect, useLayoutEffect, useMemo, useState, type MouseEvent, useRef } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Menu,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Spinner,
  Subtitle1,
  Subtitle2,
  Table,
  TableBody,
  Dropdown,
  Option,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Textarea,
  Title2,
  makeStyles,
  mergeClasses,
  tokens,
  useRestoreFocusTarget,
} from '@fluentui/react-components'
import {
  ArrowDownload20Regular,
  ArrowSortDown20Regular,
  ArrowSortUp20Regular,
  Dismiss16Regular,
  Filter16Regular,
  Filter24Regular,
  Search20Regular,
  Eye20Regular,
} from '@fluentui/react-icons'
import { useDemands, useInviteTalents } from '../hooks/useDemands'
import { useTalentDirectory } from '../hooks/useTalentDirectory'
import { useToast } from '../components/ToastProvider'
import { ErrorCard } from '../components/ErrorCard'
import api from '../lib/api'
import { downloadBlob } from '../lib/download'
import { apiErrorMessage, blobErrorMessage } from '../lib/apiError'
import { FOCUS_AREAS, focusAreaWithName } from '../types/focus_area'
import type { Demand, DemandStage } from '../types/demand'
import { DemandIntakeForm } from '../components/DemandIntakeForm'
import { useEffectiveUser } from '../hooks/useEffectiveUser'
import { demandDetailPath, openDemandInNewTab, isNewTabModifier } from '../lib/demandNav'
import { useOpenInNewTabMenu, OpenInNewTabMenu } from '../components/OpenInNewTabMenu'
import { NewDemandSplitButton } from '../components/NewDemandSplitButton'
import { bayerColors, bucketColors } from '../theme'
import {
  DEMAND_TYPE_LABELS,
  PRIORITY_BUCKETS,
  STAGE_LABELS,
  SUPPORT_MODE_LABELS,
  priorityBucketColor,
  priorityTierLabel,
} from '../lib/labels'
import { STAFFING_FILTER_OPTIONS } from '../lib/demandFilters'
import { useFaTags } from '../hooks/useFocusAreas'
import { daysInStage, slaTone } from '../lib/demandSla'
import { DemandPreviewDrawer } from '../components/DemandPreviewDrawer'
import { recalledDemandList, rememberDemandList } from '../lib/demandListReturn'
import { useTestDataVisibility } from '../context/TestDataVisibilityContext'
import { TitleWithHelp } from '../components/TitleWithHelp'
import { CaretakerFilter } from '../components/CaretakerFilter'

/**
 * Demand List view — mockup §6663-6824, Issue #715.
 *
 * RBAC scoping:
 * - Council member: auto-filtered to own FA, and may widen it to All
 * - Admin/super_admin: full list with FA column + FA filter in column header
 * - `views_all_demands`: the same list, read-only — no bulk invite, no row menu
 * - Impersonation: follows impersonated identity via useEffectiveUser()
 *
 * Filtering and sorting, as they stand now:
 * - Every narrowing is a SERVER filter (#1269), so `total` describes the
 *   filtered set rather than what happened to load.
 * - Column headings sort; Shift-click chains a second column. The chain,
 *   the page and the filters all live in the URL so a view can be linked.
 * - The toolbar carries search and priority; **More filters** holds type,
 *   support mode, staffing and tags. This REPLACES the earlier rule that no
 *   filter may live outside a column heading — six filters did not fit there.
 * - Only `last_activity` sorting is sent to the server — and it is the default
 *   order, newest first. The other columns are sorted in the browser, which is
 *   the whole set at one page and this page only beyond it — `sortsWithinPage`
 *   below says so out loud.
 */

interface StageChip {
  key: '' | DemandStage
  label: string
  dotColor: string
}

const STAGE_CHIPS: StageChip[] = [
  { key: '', label: 'All stages', dotColor: 'transparent' },
  { key: 'new', label: 'New', dotColor: '#64748b' },
  { key: 'scoping', label: 'Scoping', dotColor: '#4f7cac' },
  { key: 'triaging', label: 'Triaging', dotColor: '#f59e0b' },
  { key: 'resourcing', label: 'Resourcing', dotColor: '#0891b2' },
  { key: 'parking', label: STAGE_LABELS.parking, dotColor: bucketColors.declined },
  { key: 'closed', label: 'Closed', dotColor: bucketColors.quickWins },
]

const STAGE_DOT_COLORS: Record<DemandStage, string> = {
  new: '#64748b',
  under_review: '#f59e0b',
  open_to_application: '#0891b2',
  staffed: '#8b5cf6',
  not_prioritized: bucketColors.declined,
  resolved: bucketColors.quickWins,
  // New-model stages (FE expand, Inc-3).
  scoping: '#4f7cac',
  triaging: '#f59e0b',
  resourcing: '#0891b2',
  parking: bucketColors.declined,
  closed: bucketColors.quickWins,
}

// ─── Priority bucket (mockup §6778-6806 + V2 §3) ─────────────────────────────
//
// READ `demand.priority_bucket`. The server derives it
// (`effective_priority_bucket` in models/demand.py) and has been sending it as
// a computed field all along; this page ignored it and re-derived from
// `priority_tier_override` instead, with a private snake_case vocabulary.
//
// The two disagreed. The local copy never produced Parking Lot for a parked
// demand — so a parked High Priority demand read "High Priority", which is the
// stage/bucket contradiction #988 §7 removed the stored value to make
// impossible — and it echoed a stale stored `parking-lot` on active demands
// that the server deliberately ignores.
//
// Neither bucket derives from the four scoring criteria. Those are a PARALLEL,
// independent signal; scoring a demand does not move it.
const BUCKET_OPTIONS: { key: string; label: string }[] = [
  { key: '', label: 'All priority buckets' },
  ...PRIORITY_BUCKETS.map((key) => ({ key, label: priorityTierLabel(key) })),
]

// ─── Sorting ─────────────────────────────────────────────────────────────────
type SortKey = 'title' | 'stage' | 'fa' | 'bucket' | 'days' | 'activity'
type SortDir = 'asc' | 'desc'
interface SortCriterion {
  key: SortKey
  dir: SortDir
}

function readSortCriteria(value: string | null): SortCriterion[] {
  const result: SortCriterion[] = []
  for (const part of value?.split(',') ?? []) {
    const [key, dir] = part.split(':')
    if (
      (key === 'title' || key === 'stage' || key === 'fa' || key === 'bucket' || key === 'days' || key === 'activity') &&
      (dir === 'asc' || dir === 'desc') && !result.some((criterion) => criterion.key === key)
    ) result.push({ key, dir })
  }
  // Default: newest activity first — what changed since you last looked, and
  // the one order the server can give across pages. An explicit ?sort= wins.
  return result.length ? result : [{ key: 'activity', dir: 'desc' }]
}

// Stage progression order for the Stage column sort.
const STAGE_SORT_ORDER: Record<string, number> = {
  new: 0,
  scoping: 1,
  triaging: 2,
  resourcing: 3,
  parking: 4,
  closed: 5,
  // Legacy values kept for dual-read tolerance until the Inc-7 cleanup.
  under_review: 1,
  open_to_application: 3,
  staffed: 3,
  not_prioritized: 4,
  resolved: 5,
}

/**
 * Compare two demands on a single sort key (ascending). Multi-column sort
 * chains these: the first non-zero result wins, so a tie on the primary
 * column falls through to the secondary, then the tertiary, and so on.
 * `daysInStage` is a hoisted function declaration below.
 */
/**
 * Sortable form of `last_activity_at` (#1258).
 *
 * Falls back to the demand's own arrival, matching what the server does when
 * nothing has happened yet — so a row from an older API response that predates
 * the field still sorts somewhere sensible instead of to one end.
 */
function lastActivityMs(d: Demand): number {
  const iso = d.last_activity_at ?? d.created_at
  const t = iso ? Date.parse(iso) : NaN
  return Number.isNaN(t) ? 0 : t
}

/** Whole days since an ISO timestamp — the Last activity column's unit. */
function daysSince(iso: string | null | undefined): number {
  const t = iso ? Date.parse(iso) : NaN
  if (Number.isNaN(t)) return 0
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000))
}

function compareByKey(a: Demand, b: Demand, key: SortKey): number {
  switch (key) {
    case 'title':
      return a.title.localeCompare(b.title)
    case 'stage':
      return (STAGE_SORT_ORDER[a.stage ?? ''] ?? 99) - (STAGE_SORT_ORDER[b.stage ?? ''] ?? 99)
    case 'fa':
      return (a.focus_area_code ?? '').localeCompare(b.focus_area_code ?? '')
    case 'bucket':
      return (a.priority_bucket ?? '').localeCompare(b.priority_bucket ?? '')
    case 'days':
      return (
        daysInStage(a) - daysInStage(b)
      )
    case 'activity':
      // Oldest first when ascending — the point of the column is finding what
      // has been left alone longest, so the default click answers that.
      return lastActivityMs(a) - lastActivityMs(b)
    default:
      return 0
  }
}

// ─── Mockup colors (pixel-matched) ──────────────────────────────────────────
// Button primary maps to the Bayer brand-80 shade (#4c6a80).
// Page title is a near-black charcoal; tokens.colorNeutralForeground1
// (#242424) is the matching Fluent value.
const BRAND_PRIMARY = bayerColors.navyMid
const TITLE_COLOR = '#242424'

// Days-in-stage + SLA tones come from lib/demandSla (#1156) — this file's
// private table was one of four disagreeing copies.

const useStyles = makeStyles({
  root: {
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
  },
  pageHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacingVerticalL,
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  kpiStrip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalL,
    '@media (max-width: 720px)': { gridTemplateColumns: '1fr 1fr' },
  },
  kpi: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '10px 14px',
    background: tokens.colorNeutralBackground2,
    border: '1px solid transparent',
    borderRadius: tokens.borderRadiusMedium,
    textAlign: 'left',
  },
  kpiAction: {
    position: 'relative',
    cursor: 'pointer',
    fontFamily: 'inherit',
    ':hover': { background: tokens.colorNeutralBackground1Hover },
    ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: '2px' },
  },
  kpiKey: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontWeight: 600,
  },
  kpiVal: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: 600,
    lineHeight: '1.05',
    fontVariantNumeric: 'tabular-nums',
    color: tokens.colorNeutralForeground1,
  },
  kpiGo: {
    position: 'absolute',
    top: '12px',
    right: '14px',
    color: bayerColors.navy,
    fontWeight: 700,
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    marginBottom: 0,
    padding: tokens.spacingHorizontalM,
    background: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  listSurface: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    overflow: 'hidden',
  },
  resultsBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    flexWrap: 'wrap', gap: tokens.spacingHorizontalS,
    minHeight: '42px', padding: `0 ${tokens.spacingHorizontalM}`,
    color: tokens.colorNeutralForeground3,
  },
  resultsActions: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  previewButton: { flexShrink: 0, color: tokens.colorNeutralForeground3 },
  extraFilters: {
    flexBasis: '100%', order: 1,
    display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalS,
    '&[hidden]': { display: 'none' },
  },
  searchWrap: {
    flex: '1 1 240px',
    minWidth: '200px',
    '& .fui-Input': { width: '100%' },
  },
  tableWrap: {
    background: tokens.colorNeutralBackground1,
    // Scroll the table inside its own box on a narrow window instead of
    // clipping the right-hand columns (Days / Applications) out of reach.
    overflowX: 'auto',
  },
  tableRow: {
    cursor: 'pointer',
    ':hover': { background: tokens.colorNeutralBackground1Hover },
    ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: '-2px' },
    '& > td': { paddingTop: '12px', paddingBottom: '12px', borderBottom: `1px solid ${tokens.colorNeutralStroke3}` },
  },
  selectedRow: {
    background: tokens.colorBrandBackground2,
    boxShadow: `inset 3px 0 ${tokens.colorBrandStroke1}`,
    ':hover': { background: tokens.colorBrandBackground2Hover },
  },
  rowTitle: {
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    textDecoration: 'none',
    overflowWrap: 'anywhere',
    ':hover': { textDecoration: 'underline', color: tokens.colorBrandForegroundLink },
    ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: '2px' },
  },
  rowMeta: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
    marginTop: '2px',
  },
  stageCell: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
  },
  emptyCell: {
    color: tokens.colorNeutralForeground3,
  },
  daysNeutral: {
    color: tokens.colorNeutralForeground2,
  },
  daysAmber: {
    color: tokens.colorPaletteDarkOrangeForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  daysRed: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  urgencyDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: '6px',
  },
  stageDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    display: 'inline-block',
  },
  sortableHeader: {
    border: 0,
    padding: '8px 2px',
    background: 'transparent',
    font: 'inherit',
    color: 'inherit',
    cursor: 'pointer',
    userSelect: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    ':hover': { color: BRAND_PRIMARY },
    ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: '2px' },
  },
  sortIcon: {
    opacity: 0.6,
    fontSize: '14px',
  },
  sortRank: {
    fontSize: '10px',
    fontWeight: 700,
    lineHeight: 1,
    color: BRAND_PRIMARY,
    background: tokens.colorNeutralBackground1Hover,
    borderRadius: '8px',
    padding: '1px 4px',
    marginLeft: '1px',
  },
  headerFilterBtn: {
    border: 0,
    background: 'transparent',
    display: 'inline-flex',
    alignItems: 'center',
    padding: '6px',
    marginLeft: '2px',
    borderRadius: tokens.borderRadiusSmall,
    cursor: 'pointer',
    color: tokens.colorNeutralForeground3,
    ':hover': { color: BRAND_PRIMARY, background: tokens.colorNeutralBackground1Hover },
    ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: '2px' },
  },
  headerFilterActive: {
    color: BRAND_PRIMARY,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 16px',
    gap: tokens.spacingVerticalS,
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground3,
  },
  loadingState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 16px',
    gap: tokens.spacingHorizontalS,
  },
})


function stageLabel(stage: DemandStage): string {
  return STAGE_LABELS[stage] ?? stage
}

function stageDotColor(stage: DemandStage): string {
  return STAGE_DOT_COLORS[stage] ?? bucketColors.declined
}

type DemandListUrlFilter = '' | 'no-applications' | 'stale-14d' | 'caretaking'

function normalizeStageParam(value: string | null): '' | DemandStage {
  if (!value) return ''
  return STAGE_CHIPS.some((chip) => chip.key === value) ? (value as DemandStage) : ''
}

function normalizeDemandListFilter(value: string | null): DemandListUrlFilter {
  if (value === 'no-applications' || value === 'stale-14d' || value === 'caretaking') return value
  return ''
}

export function DemandList() {
  const styles = useStyles()
  const navigate = useNavigate()
  const location = useLocation()
  const restoreFocusTarget = useRestoreFocusTarget()
  const effectiveUser = useEffectiveUser()
  const { showTestData } = useTestDataVisibility()
  const viewerScope = `${effectiveUser?.id}:${effectiveUser?.role}:${effectiveUser?.focus_area_code}:${effectiveUser?.views_all_demands}:${showTestData}`
  const restoredEntry = useRef<string | null>(null)
  const previewOrigin = useRef<{ scrollY: number; focusId: string } | null>(null)
  const [previewId, setPreviewId] = useState<number | null>(null)

  const isAdmin =
    effectiveUser?.role === 'admin' || effectiveUser?.role === 'super_admin'
  const canManage = isAdmin || effectiveUser?.role === 'council'
  const [searchParams, setSearchParams] = useSearchParams()
  // Council defaults to their own FA, but can switch to any FA / all.
  const ownFa = effectiveUser?.focus_area_code ?? ''
  const listFilter = normalizeDemandListFilter(searchParams.get('filter'))

  // Filters live in the URL, so Back restores the exact view and a copied link
  // reproduces it. Writes use `replace` to keep filter tweaks out of history.
  const setUrlParam = useCallback(
    (key: string, value: string) => {
      if ((searchParams.get(key) ?? '') === value) return
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (value) next.set(key, value)
          else next.delete(key)
          if (key !== 'page') next.delete('page')
          return next
        },
        { replace: true },
      )
    },
    [searchParams, setSearchParams],
  )

  // Stage / FA / bucket derive straight from the query string (no local mirror,
  // so a stale URL can never clobber a fresh selection). Their setters write it.
  const stage = normalizeStageParam(searchParams.get('stage'))
  const focusArea = searchParams.get('focusArea') ?? (isAdmin ? '' : ownFa)
  // Validated against the canonical list so a hand-edited URL cannot filter to
  // a bucket that does not exist (which would silently show an empty table).
  const bucketParam = searchParams.get('bucket') ?? ''
  const bucketFilter = PRIORITY_BUCKETS.includes(bucketParam) ? bucketParam : ''
  const setStage = useCallback((v: string) => setUrlParam('stage', v), [setUrlParam])
  // FA also clears the tag param — tag ids are FA-scoped, so a tag carried
  // across an FA switch could only ever match nothing.
  const setFocusArea = useCallback(
    (v: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('focusArea', v)
          next.delete('tag')
          next.delete('page')
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )
  const setBucketFilter = useCallback((v: string) => setUrlParam('bucket', v), [setUrlParam])
  const caretakerFilter = searchParams.get('caretaker') ?? ''
  const setCaretakerFilter = (selected: string[]) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (selected.length) next.set('caretaker', selected.join(','))
      else next.delete('caretaker')
      if (next.get('filter') === 'caretaking') next.delete('filter')
      next.delete('page')
      return next
    }, { replace: true })
  }

  // Field filters (#1242) — URL-addressable like the rest, validated against
  // their catalogs so a hand-edited URL degrades to "no filter", never to a
  // silently empty table.
  const typeParam = searchParams.get('type') ?? ''
  const typeFilter = typeParam in DEMAND_TYPE_LABELS ? typeParam : ''
  const supportParam = searchParams.get('support') ?? ''
  const supportFilter = supportParam in SUPPORT_MODE_LABELS ? supportParam : ''
  const staffingParam = searchParams.get('staffing') ?? ''
  const staffingFilter = STAFFING_FILTER_OPTIONS.some((o) => o.key === staffingParam)
    ? staffingParam
    : ''
  const tagParam = searchParams.get('tag') ?? ''
  const tagFilter = /^\d+$/.test(tagParam) ? Number(tagParam) : null
  const setTypeFilter = useCallback((v: string) => setUrlParam('type', v), [setUrlParam])
  const setSupportFilter = useCallback((v: string) => setUrlParam('support', v), [setUrlParam])
  const setStaffingFilter = useCallback((v: string) => setUrlParam('staffing', v), [setUrlParam])
  const setTagFilter = useCallback(
    (v: number | null) => setUrlParam('tag', v == null ? '' : String(v)),
    [setUrlParam],
  )
  const extraFilterCount = [typeFilter, supportFilter, staffingFilter, tagFilter != null].filter(Boolean).length
  const [showExtraFilters, setShowExtraFilters] = useState(extraFilterCount > 0)
  const extraFiltersOpen = showExtraFilters

  // Search keeps a local mirror for input responsiveness, seeded from the URL
  // and debounced back into it.
  //
  // Unlike stage/FA/bucket above, this one CANNOT simply derive from the query
  // string: the write is debounced, so a derived input would lag a keystroke
  // behind the typist. The mirror is right — what was missing is adopting a
  // change that came from OUTSIDE the box. On Back the URL reverted, `search`
  // did not, and 300 ms later the debounce wrote the stale term back over the
  // navigation, so the list stayed on the old query under a URL that disagreed.
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '')
  const lastWrittenQ = useRef(searchParams.get('q') ?? '')
  const [intakeOpen, setIntakeOpen] = useState(false)
  // Multi-column sort. Plain header click sorts by that column alone;
  // Shift-click appends a column (or flips its direction if already in the
  // chain) so ties break on the next column. Rank badges show the order.
  const sortParam = searchParams.get('sort')
  const sortCriteria = useMemo(() => readSortCriteria(sortParam), [sortParam])
  // Admin-only: surface soft-deleted demands so they can be reviewed
  // and restored. Council doesn't see deleted rows at all.
  const showDeleted = isAdmin && searchParams.get('deleted') === 'true'
  // Right-click "Open in new tab" menu (shared with DemandKanban).
  const { menu: contextMenu, openMenu, closeMenu } = useOpenInNewTabMenu()

  // Everyone drives the view from the FA filter. Council defaults to their own
  // FA (above) but can browse any FA / all — other FAs are read-only (edit
  // gating is enforced per-demand + server-side).
  const effectiveFa = focusArea

  // Tag catalogs are per-FA, so the tag dropdown only means something with an
  // FA in play; without one it is disabled and any stale ?tag= is inert.
  const faTagsQuery = useFaTags(effectiveFa || undefined)
  const faTags = effectiveFa ? (faTagsQuery.data ?? []) : []

  // Debounce the search box into the URL (?q=) so each keystroke doesn't spam
  // history while still persisting the term for Back / copy-link.
  useEffect(() => {
    const t = setTimeout(() => {
      lastWrittenQ.current = search.trim()
      setUrlParam('q', search.trim())
    }, 300)
    return () => clearTimeout(t)
  }, [search, setUrlParam])

  // Adopt a `q` this component did not write — Back/Forward, or a pasted link
  // opened in the same tab. Comparing against the last value we pushed is what
  // separates "the user navigated" from "our own debounce just landed".
  const qParam = searchParams.get('q') ?? ''
  useEffect(() => {
    if (qParam !== lastWrittenQ.current) {
      lastWrittenQ.current = qParam
      setSearch(qParam)
    }
  }, [qParam])

  // Every narrowing is a SERVER filter now (#1269): the field filters and the
  // quick presets travel as query params, so `total` is the truth about the
  // filtered set rather than about what happened to load. page_size stays 500
  // — one page covers today's whole set — and when the filtered set outgrows
  // it, real pagination controls appear below instead of silent truncation.
  const pageParam = Number(searchParams.get('page') ?? 1)
  const page = Number.isSafeInteger(pageParam) && pageParam > 0 ? pageParam : 1
  const setPage = (next: number) => setUrlParam('page', next === 1 ? '' : String(next))
  const filters = useMemo(() => {
    const f: {
      search?: string
      stage?: string
      focus_area?: string
      include_deleted?: boolean
      mine_caretaker?: boolean
      caretaker?: string
      page?: number
      page_size?: number
      demand_type?: string
      support_mode?: string
      staffing?: string
      tag_id?: number
      bucket?: string
      posted_without_applications?: boolean
      stale_days?: number
      sort?: 'last_activity' | 'last_activity_asc'
    } = { page, page_size: 500 }
    if (qParam.trim()) f.search = qParam.trim()
    if (stage) f.stage = stage
    if (effectiveFa) f.focus_area = effectiveFa
    if (isAdmin && showDeleted) f.include_deleted = true
    // Caretaking is a SERVER filter (#1208): a non-council caretaker's demands
    // are usually unposted, so narrowing the visible rows client-side would
    // show nothing — the list must ASK for them, and the visibility layer's
    // caretaker branch does the rest.
    if (listFilter === 'caretaking') f.mine_caretaker = true
    else if (caretakerFilter) f.caretaker = caretakerFilter
    if (listFilter === 'no-applications') f.posted_without_applications = true
    if (listFilter === 'stale-14d') f.stale_days = 14
    if (typeFilter) f.demand_type = typeFilter
    if (supportFilter) f.support_mode = supportFilter
    if (staffingFilter) f.staffing = staffingFilter
    if (tagFilter != null) f.tag_id = tagFilter
    if (bucketFilter) f.bucket = bucketFilter
    // When Last activity leads the sort, the ORDER must come from the server
    // too — a browser sort of one page is only right within that page (#1258).
    const primary = sortCriteria[0]
    if (primary?.key === 'activity') {
      f.sort = primary.dir === 'asc' ? 'last_activity_asc' : 'last_activity'
    }
    return f
  }, [
    qParam,
    stage,
    effectiveFa,
    isAdmin,
    showDeleted,
    listFilter,
    caretakerFilter,
    typeFilter,
    supportFilter,
    staffingFilter,
    tagFilter,
    bucketFilter,
    sortCriteria,
    page,
  ])

  const { data, isLoading, isFetching, isPlaceholderData, error, refetch } = useDemands(filters, viewerScope)
  const updating = !!isFetching || search.trim() !== qParam

  const allRows: Demand[] = useMemo(() => data?.items ?? [], [data])
  const total = data?.total ?? 0

  // The narrowing happened server-side (#1269) — what arrives IS the
  // filtered set (or one page of it, when it outgrows page_size).
  const filteredRows = allRows

  // Client-side sort. Walk the criteria chain; the first column that
  // distinguishes the two rows decides the order.
  const rows = useMemo(() => {
    const sorted = [...filteredRows]
    sorted.sort((a, b) => {
      for (const { key, dir } of sortCriteria) {
        const cmp = compareByKey(a, b, key)
        if (cmp !== 0) return dir === 'asc' ? cmp : -cmp
      }
      return 0
    })
    return sorted
  }, [filteredRows, sortCriteria])

  // A browser sort can only order what arrived. Once the filtered set spans
  // pages the SERVER chose which rows are on this one, by its own order, so a
  // column sort is true within the page and nowhere else. Say it rather than
  // let the heading imply a list-wide order. `activity` is exempt: that one is
  // sent to the server (#1258), so its pages are ordered before they arrive.
  const sortsWithinPage = total > rows.length && sortCriteria[0]?.key !== 'activity'

  useLayoutEffect(() => {
    if (isLoading || isPlaceholderData || !data || restoredEntry.current === location.key) return
    restoredEntry.current = location.key
    const saved = recalledDemandList(location.key, viewerScope)
    if (!saved || saved.url !== location.pathname + location.search) return
    document.getElementById(saved.focusId)?.focus({ preventScroll: true })
    window.scrollTo({ top: saved.scrollY, behavior: 'instant' })
  }, [data, isLoading, isPlaceholderData, location.key, location.pathname, location.search, viewerScope])

  // A filter or identity change must not leave a preview of the previous set.
  useEffect(() => {
    setPreviewId(null)
  }, [location.search, viewerScope])
  const previewIndex = rows.findIndex((row) => row.id === previewId)
  const preview = !isPlaceholderData && !error && previewIndex >= 0 ? rows[previewIndex] : undefined

  function openDetails(id: number, focusId = `demand-row-${id}`, scrollY = window.scrollY) {
    const position = {
      key: location.key,
      url: location.pathname + location.search,
      scope: viewerScope,
      scrollY,
      focusId,
    }
    rememberDemandList(position)
    navigate(demandDetailPath(id), { state: { demandListReturn: position } })
  }

  const handleSort = useCallback((key: SortKey, additive: boolean) => {
    const nextCriteria = (() => {
      const prev = sortCriteria
      const idx = prev.findIndex((c) => c.key === key)
      if (additive) {
        // Shift-click: extend the sort chain, or flip this column's
        // direction if it's already in it. Order is preserved.
        if (idx === -1) return [...prev, { key, dir: 'asc' as const }]
        const next = [...prev]
        next[idx] = { key, dir: prev[idx]?.dir === 'asc' ? 'desc' : 'asc' }
        return next
      }
      // Plain click: collapse to a single-column sort. Toggle direction
      // only when this column is already the sole sort.
      if (prev.length === 1 && idx === 0) {
        return [{ key, dir: prev[0]?.dir === 'asc' ? 'desc' : 'asc' }]
      }
      return [{ key, dir: 'asc' }]
    })()
    setUrlParam('sort', nextCriteria.map((criterion) => `${criterion.key}:${criterion.dir}`).join(','))
  }, [sortCriteria, setUrlParam])

  // Sort indicator (arrow + rank badge) for a column header. Rank shows only
  // when more than one column is active, so single-sort stays uncluttered.
  const renderSortAffordance = (key: SortKey) => {
    const crit = sortCriteria.find((c) => c.key === key)
    if (!crit) return null
    const idx = sortCriteria.indexOf(crit)
    const dir = crit.dir
    return (
      <>
        {dir === 'asc' ? (
          <ArrowSortUp20Regular className={styles.sortIcon} />
        ) : (
          <ArrowSortDown20Regular className={styles.sortIcon} />
        )}
        {sortCriteria.length > 1 && (
          <span className={styles.sortRank} data-testid={`sort-rank-${key}`}>
            {idx + 1}
          </span>
        )}
      </>
    )
  }

  // Pending decisions = demands in the review band (scoping/triaging) awaiting
  // a council decision.
  //
  // Counted over `rows` — what the table below actually lists — not `allRows`.
  // It shares one sentence with "{rows.length} of {total} demands", and taking
  // the two halves from different sets made the filtered view claim pending
  // decisions that were nowhere on screen. (The KPI block below is the opposite
  // case on purpose: it reads the whole portfolio and hides itself when a
  // filter is on.)
  const pendingDecisions = useMemo(
    () => rows.filter((d) => d.stage === 'scoping' || d.stage === 'triaging').length,
    [rows],
  )

  // "At a glance" KPIs, computed from the loaded rows. Shown only on the
  // unfiltered view so the counts read as the FA's whole portfolio (a stage
  // or search filter would narrow allRows and distort them).
  const kpis = useMemo(() => {
    const active = allRows.filter((d) => d.stage !== 'closed').length
    const awaiting = allRows.filter((d) => d.stage === 'new').length
    const openCount = allRows.filter((d) => d.stage === 'resourcing').length
    const activeRows = allRows.filter(
      (d) => d.stage !== 'closed' && d.stage !== 'parking',
    )
    const days = activeRows
      .map((d) => daysInStage(d))
      .sort((a, b) => a - b)
    const median = days.length ? days[Math.floor((days.length - 1) / 2)] ?? 0 : 0
    return { active, awaiting, openCount, median }
  }, [allRows])

  const hasActiveFilters = !!(
    search ||
    stage ||
    focusArea ||
    bucketFilter ||
    typeFilter ||
    supportFilter ||
    staffingFilter ||
    tagFilter != null ||
    caretakerFilter ||
    listFilter
  )

  const handleClearFilters = () => {
    setSearch('')
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        for (const k of [
          'q',
          'stage',
          'focusArea',
          'bucket',
          'filter',
          'caretaker',
          'type',
          'support',
          'staffing',
          'tag',
          'page',
        ])
          next.delete(k)
        next.set('focusArea', '')
        return next
      },
      { replace: true },
    )
  }

  const handleNewDemand = () => {
    setIntakeOpen(true)
  }

  // ── Bulk talent invite mode ──────────────────────────────────────────────
  // When arriving from Talent Directory with `?invite_talents=1,2,3`, the
  // page switches into a "pick a demand to invite N talents to" mode.
  // Row click then opens a confirmation dialog instead of navigating.
  const inviteTalentIds = useMemo(() => {
    const raw = searchParams.get('invite_talents')
    if (!raw) return [] as number[]
    return raw
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isFinite(n) && n > 0)
  }, [searchParams])
  const inInviteMode = canManage && inviteTalentIds.length > 0
  const inviteTalents = useInviteTalents()
  const { showToast } = useToast()
  const [inviteTarget, setInviteTarget] = useState<Demand | null>(null)
  const [inviteNote, setInviteNote] = useState('')
  const [exporting, setExporting] = useState(false)

  // Download the current (filtered) demand view as an .xlsx from the backend.
  async function exportDemands() {
    setExporting(true)
    try {
      const params: Record<string, string> = {}
      if (stage) params.stage = stage
      if (effectiveFa) params.focus_area = effectiveFa
      if (search.trim()) params.search = search.trim()
      if (listFilter === 'caretaking') params.mine_caretaker = 'true'
      else if (caretakerFilter) params.caretaker = caretakerFilter
      params.include_test = String(showTestData)
      const res = await api.get('/demands/export', { params, responseType: 'blob' })
      downloadBlob('daccelerate-demands.xlsx', res.data as Blob)
    } catch (e) {
      showToast({ title: 'Export failed', body: await blobErrorMessage(e), intent: 'error' })
    } finally {
      setExporting(false)
    }
  }

  // Resolve talent names for the invite banner
  const { data: talentData } = useTalentDirectory({ enabled: inInviteMode })
  const inviteTalentNames = useMemo(() => {
    if (!inInviteMode || !talentData?.items) return ''
    const names = inviteTalentIds
      .map((id) => talentData.items.find((t) => t.id === id)?.name)
      .filter(Boolean)
    if (names.length === 0) return `${inviteTalentIds.length} talent${inviteTalentIds.length !== 1 ? 's' : ''}`
    if (names.length <= 3) return names.join(', ')
    return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`
  }, [inInviteMode, inviteTalentIds, talentData])

  const cancelInviteMode = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('invite_talents')
      return next
    })
  }

  const confirmInvite = () => {
    if (!inviteTarget) return
    inviteTalents.mutate(
      { demandId: inviteTarget.id, talentIds: inviteTalentIds, note: inviteNote.trim() || undefined },
      {
        onSuccess: (data) => {
          showToast({
            title: `Invited ${data.invited.length} of ${data.total_requested}`,
            body:
              data.skipped_already_invited.length > 0
                ? `Skipped ${data.skipped_already_invited.length} already-invited talents.`
                : undefined,
            intent: 'success',
          })
          setInviteTarget(null)
          setInviteNote('')
          // Drop the invite_talents param + jump to demand detail
          cancelInviteMode()
          navigate(`/demand/${inviteTarget.id}`)
        },
        onError: (err) => {
          showToast({ title: 'Invite failed', body: apiErrorMessage(err), intent: 'error' })
        },
      },
    )
  }

  const handleRowClick = (id: number, event?: MouseEvent<HTMLElement>) => {
    if (inInviteMode && updating) return
    if (event && isNewTabModifier(event)) {
      event.preventDefault()
      event.stopPropagation()
      openDemandInNewTab(id)
      return
    }
    if (inInviteMode) {
      const demand = rows.find((r) => r.id === id)
      if (demand) {
        setInviteTarget(demand)
        setInviteNote('')
      }
      return
    }
    openDetails(id)
  }

  const handleRowContextMenu = (id: number, event: MouseEvent<HTMLElement>) => {
    openMenu(id, event)
  }

  return (
    <section className={styles.root} aria-label="Demand list">
      <header className={styles.pageHead}>
        <div>
          <TitleWithHelp topic="demandList">
            <Title2 as="h1" style={{ color: TITLE_COLOR }}>Demand List</Title2>
          </TitleWithHelp>
        </div>
        <div style={{ display: 'flex', gap: tokens.spacingHorizontalS }}>
          {canManage && <NewDemandSplitButton onIntake={handleNewDemand} />}
        </div>
      </header>

      {!inInviteMode &&
        !stage &&
        !search.trim() &&
        !bucketFilter &&
        !listFilter &&
        !caretakerFilter &&
        !typeFilter &&
        !supportFilter &&
        !staffingFilter &&
        tagFilter == null &&
        !isPlaceholderData &&
        total === allRows.length &&
        allRows.length > 0 && (
          <div className={styles.kpiStrip} data-testid="demand-kpis">
            <div className={styles.kpi}>
              <span className={styles.kpiKey}>Active demands</span>
              <span className={styles.kpiVal}>{kpis.active}</span>
            </div>
            <button
              type="button"
              className={mergeClasses(styles.kpi, styles.kpiAction)}
              onClick={() => setStage('new')}
              data-testid="kpi-awaiting-triage"
            >
              <span className={styles.kpiGo} aria-hidden>
                →
              </span>
              <span className={styles.kpiKey}>Awaiting triage</span>
              <span className={styles.kpiVal}>{kpis.awaiting}</span>
            </button>
            <div className={styles.kpi}>
              <span className={styles.kpiKey}>Resourcing</span>
              <span className={styles.kpiVal}>{kpis.openCount}</span>
            </div>
            <div className={styles.kpi}>
              <span className={styles.kpiKey}>Median days in stage</span>
              <span className={styles.kpiVal}>{kpis.median}</span>
            </div>
          </div>
        )}

      {inInviteMode && (
        <div
          role="region"
          aria-label="Invite mode"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: tokens.spacingHorizontalM,
            padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
            background: bayerColors.navyLight,
            border: `1px solid ${bayerColors.navy}`,
            borderRadius: tokens.borderRadiusMedium,
            color: bayerColors.navy,
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          <span>
            Pick a demand to invite <strong>{inviteTalentNames}</strong> to.
          </span>
          <div style={{ flex: 1 }} />
          <Button size="small" appearance="subtle" onClick={cancelInviteMode}>
            Cancel
          </Button>
        </div>
      )}

      <div className={styles.listSurface}>
      <div className={styles.toolbar} data-testid="demand-list-toolbar">
        <div className={styles.searchWrap}>
          <Input
            placeholder="Search demands"
            value={search}
            onChange={(_, d) => setSearch(d.value)}
            contentBefore={<Search20Regular />}
            aria-label="Search demands"
            data-testid="demand-search"
          />
        </div>

        <Button
          appearance={listFilter === 'caretaking' ? 'primary' : 'subtle'}
          size="small"
          onClick={() =>
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev)
                if (listFilter === 'caretaking') next.delete('filter')
                else next.set('filter', 'caretaking')
                next.delete('caretaker')
                next.delete('page')
                return next
              },
              { replace: true },
            )
          }
          data-testid="filter-caretaking"
          aria-pressed={listFilter === 'caretaking'}
          title="Demands you caretake"
        >
          My caretaking
        </Button>
        <CaretakerFilter
          selected={caretakerFilter ? caretakerFilter.split(',') : []}
          onChange={setCaretakerFilter}
          focusArea={effectiveFa}
          includeDeleted={showDeleted}
        />

        {/* Field filters (#1242) — same dimensions as the Kanban, shared rule
            in lib/demandFilters; all URL-addressable. The bucket filter
            existed (workspace drill-downs set ?bucket=) but had no on-page
            control until now. */}
        <Dropdown
          placeholder="All priorities"
          value={bucketFilter ? priorityTierLabel(bucketFilter) : ''}
          selectedOptions={bucketFilter ? [bucketFilter] : []}
          onOptionSelect={(_, d) => setBucketFilter(d.optionValue ?? '')}
          aria-label="Filter by priority bucket"
          data-testid="list-filter-bucket"
          size="small"
          style={{ minWidth: '140px' }}
        >
          <Option value="" text="All priorities" key="__all__">
            All priorities
          </Option>
          {PRIORITY_BUCKETS.map((key) => (
            <Option value={key} text={priorityTierLabel(key)} key={key}>
              {priorityTierLabel(key)}
            </Option>
          ))}
        </Dropdown>
        <Button
          appearance="subtle"
          size="small"
          icon={<Filter16Regular />}
          aria-expanded={extraFiltersOpen}
          aria-controls="demand-extra-filters"
          onClick={() => setShowExtraFilters((previous) => !previous)}
        >
          More filters{extraFilterCount > 0 ? ` (${extraFilterCount})` : ''}
        </Button>
        <div id="demand-extra-filters" className={styles.extraFilters} hidden={!extraFiltersOpen}>
        <Dropdown
          placeholder="All types"
          value={typeFilter ? (DEMAND_TYPE_LABELS[typeFilter] ?? typeFilter) : ''}
          selectedOptions={typeFilter ? [typeFilter] : []}
          onOptionSelect={(_, d) => setTypeFilter(d.optionValue ?? '')}
          aria-label="Filter by demand type"
          data-testid="list-filter-type"
          size="small"
          style={{ minWidth: '120px' }}
        >
          <Option value="" text="All types" key="__all__">
            All types
          </Option>
          {Object.entries(DEMAND_TYPE_LABELS).map(([key, label]) => (
            <Option value={key} text={label} key={key}>
              {label}
            </Option>
          ))}
        </Dropdown>
        <Dropdown
          placeholder="All support modes"
          value={supportFilter ? (SUPPORT_MODE_LABELS[supportFilter] ?? supportFilter) : ''}
          selectedOptions={supportFilter ? [supportFilter] : []}
          onOptionSelect={(_, d) => setSupportFilter(d.optionValue ?? '')}
          aria-label="Filter by support mode"
          data-testid="list-filter-support"
          size="small"
          style={{ minWidth: '140px' }}
        >
          <Option value="" text="All support modes" key="__all__">
            All support modes
          </Option>
          {Object.entries(SUPPORT_MODE_LABELS).map(([key, label]) => (
            <Option value={key} text={label} key={key}>
              {label}
            </Option>
          ))}
        </Dropdown>
        <Dropdown
          placeholder="Staffing: all"
          value={
            staffingFilter
              ? (STAFFING_FILTER_OPTIONS.find((o) => o.key === staffingFilter)?.label ?? '')
              : ''
          }
          selectedOptions={staffingFilter ? [staffingFilter] : []}
          onOptionSelect={(_, d) => setStaffingFilter(d.optionValue ?? '')}
          aria-label="Filter by staffing"
          data-testid="list-filter-staffing"
          size="small"
          style={{ minWidth: '130px' }}
        >
          <Option value="" text="Staffing: all" key="__all__">
            Staffing: all
          </Option>
          {STAFFING_FILTER_OPTIONS.map((o) => (
            <Option value={o.key} text={o.label} key={o.key}>
              {o.label}
            </Option>
          ))}
        </Dropdown>
        <Dropdown
          placeholder={effectiveFa ? 'All tags' : 'Tags (pick an FA)'}
          value={tagFilter != null ? (faTags.find((t) => t.id === tagFilter)?.label ?? '') : ''}
          selectedOptions={tagFilter != null ? [String(tagFilter)] : []}
          onOptionSelect={(_, d) => setTagFilter(d.optionValue ? Number(d.optionValue) : null)}
          aria-label="Filter by tag"
          data-testid="list-filter-tag"
          size="small"
          disabled={!effectiveFa}
          title={effectiveFa ? undefined : 'Tag catalogs are per focus area — pick one first'}
          style={{ minWidth: '130px' }}
        >
          <Option value="" text="All tags" key="__all__">
            All tags
          </Option>
          {faTags.map((t) => (
            <Option value={String(t.id)} text={t.label} key={t.id}>
              {t.label}
            </Option>
          ))}
        </Dropdown>
        </div>

        {hasActiveFilters && (
          <Button
            appearance="subtle"
            size="small"
            icon={<Dismiss16Regular />}
            onClick={handleClearFilters}
            data-testid="clear-filters"
          >
            Clear filters
          </Button>
        )}

        {isAdmin && (
          <Button
            appearance={showDeleted ? 'primary' : 'subtle'}
            size="small"
            onClick={() => setUrlParam('deleted', showDeleted ? '' : 'true')}
            aria-pressed={showDeleted}
            data-testid="toggle-show-deleted"
            title="Include soft-deleted demands (admin only)"
          >
            {showDeleted ? 'Hide deleted' : 'Show deleted'}
          </Button>
        )}

        <Button
          appearance="subtle"
          size="small"
          icon={<ArrowDownload20Regular />}
          onClick={exportDemands}
          disabled={exporting || rows.length === 0 || updating}
          data-testid="demand-export"
          title="Export the current view to Excel (.xlsx)"
        >
          {exporting ? 'Exporting…' : 'Export'}
        </Button>
      </div>

      <div className={styles.resultsBar}>
        <Caption1 role="status" aria-live="polite">
          {isLoading ? 'Loading…' : updating ? 'Updating…' : error ? 'Could not update demands' :
            rows.length > 0 && total > rows.length
              ? `Showing ${(page - 1) * 500 + 1}–${(page - 1) * 500 + rows.length} of ${total} demands`
              : `${rows.length} of ${total} demands`}
          {!updating && !error && sortsWithinPage && ' · sorted within this page'}
          {!updating && !error && pendingDecisions > 0 && ` · ${pendingDecisions} pending decisions`}
        </Caption1>
        <div className={styles.resultsActions}>
          {(total > 500 || page > 1) && (
            <>
              <Button size="small" appearance="subtle" disabled={page <= 1 || updating}
                onClick={() => setPage(Math.max(1, page - 1))} data-testid="list-page-prev">‹ Prev</Button>
              <Caption1>Page {page}</Caption1>
              <Button size="small" appearance="subtle" disabled={page * 500 >= total || updating}
                onClick={() => setPage(page + 1)} data-testid="list-page-next">Next ›</Button>
            </>
          )}
          {updating && <Spinner size="extra-tiny" aria-label="Updating demands" />}
        </div>
      </div>

      {isLoading || (isPlaceholderData && rows.length === 0) ? (
        <div className={styles.loadingState} data-testid="demand-list-loading">
          <Spinner size="tiny" />
          <Body1>Loading demands…</Body1>
        </div>
      ) : error ? (
        <div data-testid="demand-list-error">
          <ErrorCard
            title="Couldn't load demands"
            message={apiErrorMessage(error, 'Please try again.')}
            retry={() => {
              void refetch()
            }}
          />
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.emptyState} data-testid="demand-list-empty">
          <Filter24Regular />
          {page > 1 ? (
            <>
              <Subtitle1>No demands on this page</Subtitle1>
              <Button appearance="subtle" onClick={() => setPage(1)}>First page</Button>
            </>
          ) : hasActiveFilters ? (
            <>
              <Subtitle1>No demands match your filters</Subtitle1>
              <Caption1>Try adjusting the search or clearing filters.</Caption1>
            </>
          ) : (
            <>
              <Subtitle1>No demands yet</Subtitle1>
              {canManage && <Caption1>New demands you create will show up here.</Caption1>}
            </>
          )}
        </div>
      ) : (
        <div className={styles.tableWrap} aria-busy={updating}>
          <Table
            aria-label="Demand list table"
            data-testid="demand-list-table"
            style={{ minWidth: 900 }}
          >
            <TableHeader>
              <TableRow>
                {/* Title — sort only */}
                <TableHeaderCell style={{ width: '38%' }} aria-sort={sortCriteria[0]?.key === 'title' ? (sortCriteria[0].dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button type="button"
                    className={styles.sortableHeader}
                    onClick={(e) => handleSort('title', e.shiftKey)}
                    title="Click to sort · Shift-click to add a column"
                  >
                    Title
                    {renderSortAffordance('title')}
                  </button>
                </TableHeaderCell>

                {/* Stage — sort + filter */}
                <TableHeaderCell style={{ width: '13%' }} aria-sort={sortCriteria[0]?.key === 'stage' ? (sortCriteria[0].dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button type="button"
                    className={styles.sortableHeader}
                    onClick={(e) => handleSort('stage', e.shiftKey)}
                    title="Click to sort · Shift-click to add a column"
                  >
                    Stage
                    {renderSortAffordance('stage')}
                  </button>
                  <Menu
                    checkedValues={{ stage: stage ? [stage] : [''] }}
                    onCheckedValueChange={(_, data) => {
                      const val = data.checkedItems[0] ?? ''
                      setStage(val as '' | DemandStage)
                    }}
                  >
                    <MenuTrigger disableButtonEnhancement>
                      <button type="button"
                        className={styles.headerFilterBtn}
                        aria-label="Filter by stage"
                        style={stage ? { color: BRAND_PRIMARY } : undefined}
                        data-testid="stage-filter-trigger"
                        title={stage ? `Filtered: ${STAGE_LABELS[stage]}` : 'Filter by stage'}
                      >
                        <Filter16Regular />
                      </button>
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        <MenuItemRadio name="stage" value="">All stages</MenuItemRadio>
                        {STAGE_CHIPS.filter(c => c.key !== '').map((chip) => (
                          <MenuItemRadio name="stage" value={chip.key} key={chip.key}>
                            <span className={styles.stageDot} style={{ background: chip.dotColor }} />
                            {' '}{chip.label}
                          </MenuItemRadio>
                        ))}
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                </TableHeaderCell>

                {/* FA — sort + filter. Council sees all FAs (view-only outside
                    their own); edit is gated per-demand + server-side. */}
                <TableHeaderCell style={{ width: '8%' }} aria-sort={sortCriteria[0]?.key === 'fa' ? (sortCriteria[0].dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button type="button"
                    className={styles.sortableHeader}
                    onClick={(e) => handleSort('fa', e.shiftKey)}
                    title="Click to sort · Shift-click to add a column"
                  >
                    FA
                    {renderSortAffordance('fa')}
                  </button>
                  <Menu
                    checkedValues={{ fa: focusArea ? [focusArea] : [''] }}
                    onCheckedValueChange={(_, data) => {
                      setFocusArea(data.checkedItems[0] ?? '')
                    }}
                  >
                    <MenuTrigger disableButtonEnhancement>
                      <button type="button"
                        className={styles.headerFilterBtn}
                        aria-label="Filter by focus area"
                        style={focusArea ? { color: BRAND_PRIMARY } : undefined}
                        data-testid="fa-filter-trigger"
                        title={focusArea ? `Filtered: ${focusArea}` : 'Filter by focus area'}
                      >
                        <Filter16Regular />
                      </button>
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        <MenuItemRadio name="fa" value="">All focus areas</MenuItemRadio>
                        {FOCUS_AREAS.map((fa) => (
                          <MenuItemRadio name="fa" value={fa.code} key={fa.code}>
                            {fa.code} — {fa.name}
                          </MenuItemRadio>
                        ))}
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                </TableHeaderCell>

                {/* Priority Bucket — sort + filter */}
                <TableHeaderCell style={{ width: '15%' }} aria-sort={sortCriteria[0]?.key === 'bucket' ? (sortCriteria[0].dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button type="button"
                    className={styles.sortableHeader}
                    onClick={(e) => handleSort('bucket', e.shiftKey)}
                    title="Click to sort · Shift-click to add a column"
                  >
                    Priority Bucket
                    {renderSortAffordance('bucket')}
                  </button>
                  <Menu
                    checkedValues={{ bucket: bucketFilter ? [bucketFilter] : [''] }}
                    onCheckedValueChange={(_, data) => {
                      setBucketFilter(data.checkedItems[0] ?? '')
                    }}
                  >
                    <MenuTrigger disableButtonEnhancement>
                      <button type="button"
                        className={styles.headerFilterBtn}
                        aria-label="Filter priority bucket"
                        style={bucketFilter ? { color: BRAND_PRIMARY } : undefined}
                        data-testid="bucket-filter-trigger"
                        title={bucketFilter ? `Filtered: ${priorityTierLabel(bucketFilter)}` : 'Filter by priority bucket'}
                      >
                        <Filter16Regular />
                      </button>
                    </MenuTrigger>
                    <MenuPopover>
                      <MenuList>
                        {BUCKET_OPTIONS.map((opt) => (
                          <MenuItemRadio name="bucket" value={opt.key} key={opt.key || '__all__'}>
                            {opt.label}
                          </MenuItemRadio>
                        ))}
                      </MenuList>
                    </MenuPopover>
                  </Menu>
                </TableHeaderCell>

                {/* Days — sort only */}
                <TableHeaderCell style={{ width: '8%' }} aria-sort={sortCriteria[0]?.key === 'days' ? (sortCriteria[0].dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button type="button"
                    className={styles.sortableHeader}
                    onClick={(e) => handleSort('days', e.shiftKey)}
                    title="Click to sort · Shift-click to add a column"
                  >
                    Days
                    {renderSortAffordance('days')}
                  </button>
                </TableHeaderCell>

                {/* Last activity — sort only (#1258) */}
                <TableHeaderCell style={{ width: '10%' }} aria-sort={sortCriteria[0]?.key === 'activity' ? (sortCriteria[0].dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <button type="button"
                    className={styles.sortableHeader}
                    onClick={(e) => handleSort('activity', e.shiftKey)}
                    title="Anything that happened to the demand — a stage move, an application or a decision on one, an invitation, a comment, or an edit. Click to sort oldest first."
                  >
                    Last activity
                    {renderSortAffordance('activity')}
                  </button>
                </TableHeaderCell>

                {/* Applications — application count */}
                <TableHeaderCell style={{ width: '8%' }}>
                  <span title="Number of applications">
                    Applications
                  </span>
                </TableHeaderCell>
                {!inInviteMode && <TableHeaderCell style={{ width: '48px' }}>Preview</TableHeaderCell>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => {
                const bucket = row.priority_bucket ?? null
                const days = daysInStage(row)
                const tone = slaTone(row.stage, days)
                const daysClass =
                  tone === 'red'
                    ? styles.daysRed
                    : tone === 'amber'
                      ? styles.daysAmber
                      : styles.daysNeutral
                const urgencyDotColor =
                  tone === 'red' ? bucketColors.highPriority : tone === 'amber' ? '#f59e0b' : 'transparent'
                return (
                  <TableRow
                    key={row.id}
                    id={`demand-row-${row.id}`}
                    className={mergeClasses(styles.tableRow, previewId === row.id && styles.selectedRow)}
                    data-testid={`demand-row-${row.id}`}
                    onClick={(e) => handleRowClick(row.id, e)}
                    onContextMenu={(e) => handleRowContextMenu(row.id, e)}
                    onKeyDown={(e) => {
                      if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault()
                        handleRowClick(row.id)
                      }
                    }}
                    tabIndex={0}
                  >
                    <TableCell>
                      <Link
                        to={demandDetailPath(row.id)}
                        className={styles.rowTitle}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return
                          e.preventDefault()
                          handleRowClick(row.id)
                        }}
                        style={
                          row.deleted_at
                            ? { textDecoration: 'line-through', color: '#9e9e9e' }
                            : undefined
                        }
                      >
                        {row.title}
                      </Link>
                      <div className={styles.rowMeta}>
                        {row.demand_id ?? `#${row.id}`}
                        {row.requestor_name ? ` · ${row.requestor_name}` : ''}
                        {` · Caretaker: ${row.caretaker_name ?? 'Unassigned'}`}
                      </div>
                      {(row.tags ?? []).length > 0 && (
                        <div
                          style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}
                          data-testid={`demand-row-tags-${row.id}`}
                        >
                          {(row.tags ?? []).map((t) => (
                            <Badge key={t.id} appearance="outline" color="subtle" size="small">
                              {t.label}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {row.deleted_at && (
                        <div
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            marginTop: 4,
                            padding: '2px 8px',
                            borderRadius: 10,
                            background: '#ffebee',
                            color: '#c62828',
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: '.04em',
                          }}
                          data-testid={`demand-deleted-flag-${row.id}`}
                          title={row.deletion_reason ?? 'Soft-deleted'}
                        >
                          DELETED
                        </div>
                      )}
                      {row.possible_duplicate_of != null && (
                        <div
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            marginTop: 4,
                            padding: '2px 8px',
                            borderRadius: 10,
                            background: '#fff3e0',
                            color: '#a25e00',
                            fontSize: 11,
                            fontWeight: 600,
                          }}
                          data-testid={`demand-dup-flag-${row.id}`}
                          title={
                            row.dup_match_score
                              ? `${Math.round(row.dup_match_score)}% title match against demand #${row.possible_duplicate_of}`
                              : undefined
                          }
                        >
                          ⚠ Possible duplicate of #{row.possible_duplicate_of}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={styles.stageCell}>
                        <span
                          className={styles.stageDot}
                          style={{ background: stageDotColor(row.stage) }}
                          aria-hidden="true"
                        />
                        <Subtitle2 as="span" style={{ fontSize: '13px', fontWeight: 500 }}>
                          {stageLabel(row.stage)}
                        </Subtitle2>
                      </span>
                    </TableCell>
                    <TableCell>
                      <Caption1 title={focusAreaWithName(row.focus_area_code)} className={styles.emptyCell}>
                        {row.focus_area_code}
                      </Caption1>
                    </TableCell>
                    <TableCell data-testid={`demand-row-bucket-${row.id}`}>
                      {bucket ? (
                        <span
                          data-bucket={bucket}
                          style={{
                            fontSize: tokens.fontSizeBase200,
                            color: tokens.colorNeutralForeground2,
                          }}
                        >
                          <span className={styles.stageDot} style={{ background: priorityBucketColor(bucket) ?? undefined, marginRight: 6 }} aria-hidden />
                          {priorityTierLabel(bucket)}
                        </span>
                      ) : (
                        <Caption1 className={styles.emptyCell}>—</Caption1>
                      )}
                    </TableCell>
                    <TableCell>
                      <Caption1
                        className={daysClass}
                        data-tone={tone}
                        data-testid={`demand-row-days-${row.id}`}
                      >
                        <span
                          className={styles.urgencyDot}
                          style={{ background: urgencyDotColor }}
                          aria-hidden="true"
                        />
                        {days}d
                      </Caption1>
                    </TableCell>
                    <TableCell>
                      {/* Days, not a relative phrase, and deliberately the same
                          unit as the Days column beside it so the two can be
                          read against each other — "40d in stage, 2d since
                          anyone touched it" is a different demand from "40d and
                          40d".

                          `formatRelative` is the house formatter and is wrong
                          here: past a week it falls back to a locale DATE, so
                          the neglected demands — the entire point of this
                          column — would be the ones making you do arithmetic. */}
                      <Caption1
                        className={styles.emptyCell}
                        data-testid={`demand-row-activity-${row.id}`}
                        title={
                          row.last_activity_at
                            ? new Date(row.last_activity_at).toLocaleString()
                            : undefined
                        }
                      >
                        {daysSince(row.last_activity_at ?? row.created_at)}d
                      </Caption1>
                    </TableCell>
                    <TableCell>
                      {(row.application_count ?? 0) > 0 ? (
                        <Caption1>
                          {row.application_count}
                        </Caption1>
                      ) : (
                        <Caption1 className={styles.emptyCell}>—</Caption1>
                      )}
                    </TableCell>
                    {!inInviteMode && (
                      <TableCell>
                        <Button
                          {...restoreFocusTarget}
                          id={`demand-preview-${row.id}`}
                          className={styles.previewButton}
                          appearance="subtle"
                          size="small"
                          icon={<Eye20Regular />}
                          aria-label={`Preview ${row.title}`}
                          aria-haspopup="dialog"
                          disabled={updating}
                          onClick={(e) => {
                            e.stopPropagation()
                            previewOrigin.current = { scrollY: window.scrollY, focusId: `demand-preview-${row.id}` }
                            setPreviewId(row.id)
                          }}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
          <OpenInNewTabMenu menu={contextMenu} onClose={closeMenu} />
        </div>
      )}
      </div>

      {preview && (
        <DemandPreviewDrawer
          demand={preview}
          index={previewIndex}
          count={rows.length}
          onClose={() => setPreviewId(null)}
          onPrevious={() => setPreviewId(rows[previewIndex - 1]?.id ?? preview.id)}
          onNext={() => setPreviewId(rows[previewIndex + 1]?.id ?? preview.id)}
          onOpenDetails={() => openDetails(
            preview.id,
            previewOrigin.current?.focusId,
            previewOrigin.current?.scrollY,
          )}
        />
      )}
      <DemandIntakeForm
        open={intakeOpen}
        onClose={() => setIntakeOpen(false)}
        onSuccess={(demandId) => navigate(`/demand/${demandId}`)}
      />

      <Dialog
        open={!!inviteTarget}
        onOpenChange={(_, d) => {
          if (!d.open) {
            setInviteTarget(null)
            setInviteNote('')
          }
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Invite to this demand?</DialogTitle>
            <DialogContent>
              {inviteTarget && (
                <>
                  <Body1 style={{ marginBottom: tokens.spacingVerticalM }}>
                    Send an invitation to {inviteTalentIds.length} talent
                    {inviteTalentIds.length !== 1 ? 's' : ''} for{' '}
                    <strong>{inviteTarget.title}</strong>?
                  </Body1>
                  <Caption1 style={{ display: 'block', marginBottom: '4px' }}>
                    Optional note (added to each invitation)
                  </Caption1>
                  <Textarea
                    value={inviteNote}
                    onChange={(_, d) => setInviteNote(d.value)}
                    rows={3}
                    resize="vertical"
                    placeholder="Why you picked these talents — visible to them in their invitation."
                    aria-label="Optional invitation note"
                  />
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={() => {
                  setInviteTarget(null)
                  setInviteNote('')
                }}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={confirmInvite}
                disabled={inviteTalents.isPending}
              >
                {inviteTalents.isPending ? 'Inviting…' : 'Send invitation'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </section>
  )
}

export default DemandList
